import { cn } from '~shared/utils/cn'
import { LeadForm } from '~widgets/lead-form/components/lead-form'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'

// ─── Types ────────────────────────────────────────────────────────────────────

type TLeastProgramContentsProps = {
  program: Awaited<ReturnType<typeof getProgramData>>
  isLeadForm?: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DESCRIPTION =
  'Практики курса основаны на реальном опыте преподавателей и подкреплены результатами научных исследований'

const DEFAULT_HEADING =
  'Оставьте свои контактные данные, чтобы мы прислали **полный учебный план**'

// ─── Slugify ──────────────────────────────────────────────────────────────────

const slugify = (str: string) =>
  str
    .toLowerCase()
    .replace(/[*+~.()'"!:@]/g, '')
    .replace(/\s+/g, '-')
    .trim()

// ─── Heading with bold markdown ───────────────────────────────────────────────

const FormHeading = ({ text }: { text: string }) => {
  const parts = text.split(/\*\*(.+?)\*\*/g)
  return (
    <p className='text-[20px] font-medium leading-[120%] mb-[24px] 2lg:mb-[32px] text-center'>
      {parts.map((part, idx) =>
        idx % 2 === 1 ? (
          <span key={idx} className='text-[#3A52EE]'>
            {part}
          </span>
        ) : (
          <span key={idx}>{part}</span>
        )
      )}
    </p>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LeastProgramContentSection = ({
  program,
  isLeadForm = true
}: TLeastProgramContentsProps) => {
  const heading = program?.name ?? ''
  const leastProgramContents = program?.leastProgramContents ?? null

  return (
    <section
      id={slugify(heading)}
      className='py-[56px] 2lg:py-[80px]'
      style={{ backgroundColor: program?.faculty?.color ?? undefined }}>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
        <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-weight mb-[32px] 2lg:mb-[48px] max-w-[15ch] text-white'>
          Краткая программа курса
        </h2>

        <div
          className={cn(
            'grid grid-cols-1 gap-[40px] md:gap-[32px]',
            isLeadForm && '2lg:grid-cols-[1.3fr_1fr] 2lg:gap-[32px]'
          )}>
          {/* ─── Left: contents list ─── */}
          {leastProgramContents && leastProgramContents.length > 0 && (
            <div className='flex-1 p-[24px] md:p-[32px] rounded-[16px] bg-white text-black'>
              <p className='text-[14px] md:text-[20px] leading-[140%] mb-[24px]'>
                {DESCRIPTION}
              </p>
              <ul className='flex flex-col'>
                {leastProgramContents.map((item, idx) => (
                  <li
                    key={`LeastProgramContents__${item?.name}--${idx + 1}`}
                    className='py-[16px] border-b border-gray text-[16px] 2lg:text-[24px] font-medium leading-[120%] break-words'>
                    {item?.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ─── Right: lead form ─── */}
          {isLeadForm && (
            <div className='flex-1 p-[24px] md:p-[32px] rounded-[16px] bg-[#F5F5F5] '>
              <FormHeading text={DEFAULT_HEADING} />
              <LeadForm
                // formClassName='md:grid-cols-1'
                className='flex flex-col 2lg:gap-[30px]'
                buttonWrap={true}
                program={{
                  productId: program?.productUid ?? null,
                  productName: program?.name ?? '',
                  productCategory: program?.category?.name ?? '',
                  productFaculty: program?.faculty?.name ?? ''
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
